import PropTypes from 'prop-types';
import React from 'react';
import { lightenDarkenColor } from 'utils/colors';

interface DiamondPatternProps extends React.HTMLAttributes<SVGSVGElement> {
  colorStart?: string | null;
  colorEnd?: string | null;
}
export const DiamondPattern: React.FC<DiamondPatternProps> = ({
  colorStart,
  colorEnd,
  ...rest
}) => {
  const colorFrom = colorStart || '#49557B';
  const colorTo = colorEnd || lightenDarkenColor(colorFrom, -5);
  return (
    <svg
      viewBox="0 0 1280 336"
      xmlns="http://www.w3.org/2000/svg"
      xlinkHref="http://www.w3.org/1999/xlink"
      {...rest}
    >
      <defs>
        <linearGradient id="b" x1="0%" y1="0%" y2="91.483%">
          <stop offset="0%" stopColor={colorFrom} />
          <stop offset="100%" stopColor={colorTo} />
        </linearGradient>
        <path id="a" d="M0 0h1280v296l-824.503 20L0 286z" />
      </defs>
      <g fill="none" fillRule="evenodd">
        <g fill="#3F5570">
          <path d="M-10-3h1280v285.768L458.579 320-10 282.768z" opacity=".133" />
          <path d="M-10-3h1280v294.797L519.704 336-10 291.797z" opacity=".12" />
          <path d="M-10-3h1280v300.79L494.392 330-10 273.193z" opacity=".12" />
        </g>
        <g>
          <mask id="c" fill="#fff">
            <use xlinkHref="#a" />
          </mask>
          <use fill="url(#b)" xlinkHref="#a" />
          <g fill="#FFF" mask="url(#c)">
            <path d="M-6.916 50.166l277.109-11.4-7.783 202.387z" opacity=".16" />
            <path d="M-10.582 50.22l280.775-11.454L5.536-104.659z" opacity=".13" />
            <path d="M270.195 38.508l203.218 99.119-211.016 103.361z" opacity=".2" />
            <path d="M270.416 38.628l290.416-98.47-87.447 197.464z" opacity=".18" />
            <path d="M270.197 38.645L560.832-59.84l-274.013-65.74z" opacity=".13" />
            <path d="M270.112 38.719L182.241-8.714l92.784-.259z" opacity=".09" />
            <path d="M790 52.847L560.832-59.842l84.928 212.024z" opacity=".21" />
            <path d="M473.324 137.714l87.508-197.556 84.928 212.024z" opacity=".17" />
            <path d="M473.41 137.682l132.353 113.795 39.997-99.295z" opacity=".18" />
            <path d="M790 52.847L560.832-59.842 790-141.26z" opacity=".15" />
            <path d="M790 52.847l21.698 254.488-166.003-155.213z" opacity=".19" />
            <path d="M605.766 251.479l205.997 55.859-166.068-155.216z" opacity=".23" />
            <path d="M605.766 251.479l206.024 55.872-134.959 5.409z" opacity=".25" />
            <path d="M-10 48l272.291 193.088L-10 354.036z" opacity=".15" />
            <path d="M422.069 354.036L262.291 241.088-10 354.036z" opacity=".18" />
            <path d="M422.069 354.036L262.291 241.088l211.095-103.439z" opacity=".16" />
            <path d="M422.069 354.036h302.915l-251.588-216.42z" opacity=".21" />
          </g>
          <g fill="#FFF" mask="url(#c)">
            <path d="M790 52.853L1098.07 0l12.261 238.82z" opacity=".17" />
            <path d="M790.016 52.856L1100.196 0l-310.18-72.706z" opacity=".21" />
            <path d="M1097.861 0l239.749 100.744-227.337 138.045z" opacity=".2" />
            <path d="M1098.059 0l314.38-124.347 53.799 278.589z" opacity=".11" />
            <path d="M789.998 52.836l320.254 185.88-291.304 151.829z" opacity=".22" />
            <path d="M1298.593 348.581l-188.341-109.866-291.304 151.83z" opacity=".25" />
            <path d="M1298.593 348.581l-188.341-109.866 226.38-137.342z" opacity=".18" />
          </g>
        </g>
      </g>
    </svg>
  );
};

interface SharpDiamondPatternProps {
  color: string;
  opacity?: number;
  className?: string;
}

export const SharpDiamondPattern = ({ className, color, opacity }: SharpDiamondPatternProps) => {
  const baseOpacity = opacity || 1;
  /* This does not have the slanted bottom part, which doesn't work so well with tabbed content */
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      viewBox="0 0 1219 251"
      className={className}
    >
      <defs>
        <path id="a" d="M0 3.797L1229 .105v254H0z" />
      </defs>
      <g fill="none" fillRule="evenodd" transform="translate(0 -4)">
        <mask id="b" fill="#fff">
          <use xlinkHref="#a" />
        </mask>
        <use fill={color} xlinkHref="#a" />
        <g fill="#FFF" mask="url(#b)">
          <path d="M-7.64 70.166l266.067-11.4-7.472 202.387z" opacity={baseOpacity * 0.16} />
          <path d="M-11.16 70.22l269.587-11.454L4.315-84.66z" opacity={baseOpacity * 0.13} />
          <path d="M258.43 58.508l195.12 99.12-202.608 103.36z" opacity={baseOpacity * 0.2} />
          <path d="M258.642 58.628l278.844-98.47-83.962 197.464z" opacity={baseOpacity * 0.18} />
          <path d="M258.432 58.645L537.486-39.84l-263.095-65.74z" opacity={baseOpacity * 0.13} />
          <path d="M258.35 58.72l-84.37-47.434 89.087-.259z" opacity={baseOpacity * 0.09} />
          <path d="M757.523 72.847L537.486-39.842l81.545 212.024z" opacity={baseOpacity * 0.21} />
          <path d="M453.465 157.714l84.021-197.556 81.545 212.024z" opacity={baseOpacity * 0.17} />
          <path d="M453.548 157.682l127.08 113.795 38.403-99.295z" opacity={baseOpacity * 0.18} />
          <path d="M757.523 72.847L537.486-39.842l220.037-81.418z" opacity={baseOpacity * 0.15} />
          <path d="M757.523 72.847l20.834 254.488-159.39-155.213z" opacity={baseOpacity * 0.19} />
          <path d="M580.63 271.48l197.79 55.858-159.452-155.216z" opacity={baseOpacity * 0.23} />
          <path d="M-10.602 68l261.443 193.088-261.443 112.948z" opacity={baseOpacity * 0.15} />
          <path d="M404.252 374.036L250.841 261.088l202.684-103.44z" opacity={baseOpacity * 0.16} />
          <path d="M404.252 374.036h290.846l-241.564-216.42z" opacity={baseOpacity * 0.21} />
        </g>
        <g fill="#FFF" mask="url(#b)">
          <path d="M757.153 71.698l295.707-51.77 12.6 238.79z" opacity={baseOpacity * 0.17} />
          <path d="M757.169 71.702l297.733-51.767-298.168-73.758z" opacity={baseOpacity * 0.21} />
          <path d="M1052.658 19.927l230.617 101.542-217.87 137.217z" opacity={baseOpacity * 0.2} />
          <path d="M1052.848 19.927l301.519-123.221 52.635 278.69z" opacity={baseOpacity * 0.11} />
          <path d="M757.151 71.682l308.234 186.93-279.26 150.777z" opacity={baseOpacity * 0.22} />
          <path
            d="M1246.659 369.097l-181.274-110.485-279.26 150.777z"
            opacity={baseOpacity * 0.25}
          />
          <path
            d="M1246.659 369.097l-181.274-110.485 216.953-136.517z"
            opacity={baseOpacity * 0.18}
          />
        </g>
      </g>
    </svg>
  );
};

SharpDiamondPattern.defaultProps = {
  color: '#62789F',
};

SharpDiamondPattern.propTypes = {
  className: PropTypes.string,
  color: PropTypes.string,
  opacity: PropTypes.number,
};
